/**
 * src/context/PermissionContext.jsx  —  REPLACE EXISTING FILE
 *
 * Fixes:
 *  1. Loads permissions immediately on mount if token exists
 *  2. Reloads on login/logout via iassets_auth_change event
 *  3. Admin (role_id=1) always gets all permissions
 *  4. Exposes canView(module) shorthand for nav items
 */
import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { permissionsAPI } from '../api';

const PermissionContext = createContext(null);

export function PermissionProvider({ children }) {
  const [permissions, setPermissions] = useState({});
  const [loaded,      setLoaded]      = useState(false);

  const loadPermissions = useCallback(async () => {
    const token = localStorage.getItem('iassets_token');
    if (!token) {
      setPermissions({});
      setLoaded(true);
      return;
    }
    try {
      const { data } = await permissionsAPI.myPermissions();
      setPermissions(data.permissions || {});
    } catch {
      // If permissions API fails, don't block the app
      setPermissions({});
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    // Load on mount (covers page refresh after login)
    loadPermissions();

    // Reload on login / logout
    const handler = () => loadPermissions();
    window.addEventListener('iassets_auth_change', handler);
    return () => window.removeEventListener('iassets_auth_change', handler);
  }, [loadPermissions]);

  /**
   * can('assets', 'create')  →  boolean
   * action: 'view' | 'create' | 'edit' | 'delete' | 'export'
   *
   * Super admin (role_id === 1) always returns true.
   */
  const can = useCallback((module, action) => {
    try {
      const user = JSON.parse(localStorage.getItem('iassets_user') || '{}');
      if (user?.role_id === 1) return true;  // super admin bypass
    } catch { /* ignore */ }

    const mod = permissions[module];
    if (!mod) return false;
    return Boolean(mod[`can_${action}`]);
  }, [permissions]);

  /** Shorthand for nav visibility: canView('assets') */
  const canView = useCallback((module) => can(module, 'view'), [can]);

  return (
    <PermissionContext.Provider value={{ permissions, can, canView, loaded, reload: loadPermissions }}>
      {children}
    </PermissionContext.Provider>
  );
}

export function usePermission() {
  const ctx = useContext(PermissionContext);
  if (!ctx) throw new Error('usePermission must be used inside <PermissionProvider>');
  return ctx;
}

/**
 * <PermGate module="assets" action="delete">
 *   <button>Delete</button>
 * </PermGate>
 */
export function PermGate({ module, action = 'view', children, fallback = null }) {
  const { can } = usePermission();
  return can(module, action) ? children : fallback;
}
